import { combineRules } from 'fela';
import tinyColor from 'tinycolor2';

import { config } from '@/config/config';
import type { TRuleWithTheme } from '@/styles/theme';

import { menuDefault } from '../MenuDesktop/MenuDesktop.rules';

export const TRANSITION_DURATION = 250; // ms

const PHONE_HIDDEN_LEFT = '-100vw';

export const menuMobile: TRuleWithTheme = ({ theme }) => ({
    position: 'fixed',

    left: PHONE_HIDDEN_LEFT,
    top: 0,
    zIndex: theme.zIndexes.mobileMenu,

    width: '100vw',

    // The header row and the scroll area below own all the padding; the panel
    // itself must not scroll so the header never has to paint over content
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    overflowY: 'hidden',

    borderRightWidth: 0,

    min768: {
        left: -theme.widths.menu,

        width: theme.widths.menu,

        borderRightWidth: 1,
    },

    // The pre-enter and post-exit hidden positions come from the base `left` above —
    // overriding them in `&.enter`/`&.exit-done` would let the media-scoped rule
    // outrank `&.enter-active` in the cascade and kill the tablet slide-in.
    '&.enter-active': {
        left: 0,

        transitionProperty: 'left',
        transitionDuration: `${TRANSITION_DURATION / 1000}s`,
        transitionTimingFunction: 'ease-in-out',
    },

    '&.enter-done': {
        left: 0,
    },

    '&.exit': {
        left: 0,
    },

    '&.exit-active': {
        left: PHONE_HIDDEN_LEFT,

        transitionProperty: 'left',
        transitionDuration: `${TRANSITION_DURATION / 1000}s`,
        transitionTimingFunction: 'ease-in-out',

        min768: {
            left: -theme.widths.menu,
        },
    },
});

export const menu = combineRules(menuDefault, menuMobile);

export const menuOverlay: TRuleWithTheme = ({ theme }) => ({
    display: 'none',

    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: theme.zIndexes.mobileMenuOverlay,

    backgroundColor: [theme.colors.blue, tinyColor(theme.colors.overlayBlue).setAlpha(0.7).toRgbString()],

    min768: {
        display: 'block',
    },

    min1024: {
        backgroundColor: 'transparent',
    },
});

export const menuHeader: TRuleWithTheme = ({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',

    backgroundColor: theme.colors.white,

    paddingTop: config.demo.enable ? '0.875rem' : '1.5rem',
    paddingRight: '1rem',
    paddingBottom: config.demo.enable ? '0.875rem' : '1rem',
    paddingLeft: '1rem',

    min768: {
        paddingTop: '1.75rem',
        paddingRight: '2.5rem',
        paddingBottom: '1.75rem',
        paddingLeft: '2.5rem',
    },
});

export const panelHeader: TRuleWithTheme = () => ({
    flexShrink: 0,

    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.5rem',

    paddingTop: '1.5rem',
    paddingRight: '1.75rem',
    paddingBottom: '0.5rem',
    paddingLeft: '1.75rem',

    min768: {
        // Aligns the logo with the nav items' icons (8px gutter + 12px padding)
        paddingRight: '1.25rem',
        paddingLeft: '1.25rem',
    },
});

// Scrolls independently below the static header; its top padding plus the
// header's bottom padding form the 24px design gap, kept mostly here as
// headroom for the profile card's upward shadow, which would otherwise be
// clipped at the scrollport edge.
export const menuScroll: TRuleWithTheme = () => ({
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',

    // Side padding keeps the bleed-capable children (-1rem pull) at a 16px gutter
    paddingTop: '1rem',
    paddingRight: '2rem',
    paddingBottom: '1.5rem',
    paddingLeft: '2rem',

    MsOverflowStyle: 'none',
    scrollbarWidth: 'none',

    '&::-webkit-scrollbar': {
        display: 'block',

        width: 0,
    },

    min768: {
        paddingRight: '1.5rem',
        paddingLeft: '1.5rem',
    },
});

export const logoWrapper: TRuleWithTheme = () => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
});
